import type { AdminAuditRow } from '@trendator/shared';
import { useState } from 'react';
import { Link } from '../components/Layout';
import { Card, Empty, ErrorBox, Loading, Modal, PageHead, Table } from '../components/ui';
import { api } from '../lib/api';
import { dateTime, shortId } from '../lib/format';
import { useLoad } from '../lib/hooks';

const ACTIONS: Record<string, string> = {
  login: 'Вход в админку',
  'trend.create': 'Создал тренд',
  'trend.update': 'Изменил тренд',
  'trend.publish': 'Опубликовал тренд',
  'trend.disable': 'Выключил тренд',
  'trend.enable': 'Включил тренд',
  'trend.cover': 'Сменил обложку',
  'trend.before': 'Сменил фото «до»',
  'trend.test_run': 'Тест-прогон',
  'generation.restart': 'Перезапустил генерацию',
  'generation.refund': 'Вернул деньги за генерацию',
  'generation.view_results': 'Смотрел результаты юзера',
  'user.adjust': 'Корректировка баланса',
  'user.ban': 'Забанил',
  'user.unban': 'Снял бан',
  'user.message': 'Написал юзеру',
  'user.view_photos': 'Смотрел фото юзера',
  'user.delete_photos': 'Удалил фото юзера',
  'user.view_twin': 'Смотрел фото двойника',
  'user.delete_twin': 'Удалил двойника',
  'payout.paid': 'Отметил вывод выплаченным',
  'payout.reject': 'Отклонил вывод',
  'settings.update': 'Изменил настройки',
  'flag.view_media': 'Смотрел файлы флага',
  'flag.confirm': 'Подтвердил нарушение',
  'flag.dismiss': 'Флаг ложный',
};

function entityLink(a: AdminAuditRow): { to: string; label: string } | null {
  if (!a.entityId) return null;
  if (a.entityType === 'trend') return { to: `/trends/${a.entityId}`, label: 'тренд' };
  if (a.entityType === 'generation') return { to: `/generations?id=${a.entityId}`, label: `#${shortId(a.entityId)}` };
  if (a.entityType === 'user') return { to: `/users/${a.entityId}`, label: `юзер #${a.entityId}` };
  if (a.entityType === 'settings') return { to: '/providers', label: a.entityId };
  if (a.entityType === 'payout') return { to: '/partners?status=all', label: `вывод #${shortId(a.entityId)}` };
  if (a.entityType === 'flag') return { to: '/moderation?status=all', label: `флаг #${a.entityId}` };
  return null;
}

function summary(a: AdminAuditRow): string {
  const p = (a.payload ?? {}) as Record<string, unknown>;
  if (a.action === 'user.adjust') return `${Number(p.amount) > 0 ? '+' : ''}${String(p.amount)} ₽ · ${String(p.reason ?? '')}`;
  if (a.action === 'generation.refund') return p.freeReturned ? 'бесплатная попытка' : `${String(p.amount)} ₽`;
  if (a.action === 'trend.publish') return `v${String(p.version)} · ${String(p.price)} ₽`;
  if (a.action === 'trend.update' && p.price) {
    const pr = p.price as { from: number; to: number };
    return `цена ${pr.from} → ${pr.to} ₽`;
  }
  if (a.action === 'user.ban') return String(p.reason ?? '');
  if (a.action === 'flag.confirm') return [p.ban ? 'бан' : '', p.deleted ? `удалено файлов: ${String(p.deleted)}` : '', String(p.note ?? '')].filter(Boolean).join(' · ');
  return '';
}

export function Audit() {
  const list = useLoad(() => api.get<{ items: AdminAuditRow[] }>('/audit'));
  const [open, setOpen] = useState<AdminAuditRow | null>(null);
  return (
    <>
      <PageHead title="Аудит-лог" />
      <Card className="px-2 pt-2 pb-1">
        {list.error ? <ErrorBox text={list.error} onRetry={() => void list.reload()} /> : null}
        {!list.data && list.loading ? <Loading /> : null}
        {list.data && list.data.items.length === 0 ? <Empty>Пока пусто</Empty> : null}
        {list.data && list.data.items.length > 0 ? (
          <Table>
            <thead>
              <tr>
                <th>Когда</th>
                <th>Кто</th>
                <th>Что</th>
                <th>Где</th>
                <th>Детали</th>
              </tr>
            </thead>
            <tbody>
              {list.data.items.map((a) => {
                const l = entityLink(a);
                return (
                  <tr key={a.id} className="cursor-pointer hover:[&_td]:bg-white/3" onClick={() => setOpen(a)}>
                    <td className="text-muted">{dateTime(a.createdAt)}</td>
                    <td className="font-semibold">{a.actorName}</td>
                    <td>{ACTIONS[a.action] ?? a.action}</td>
                    <td onClick={(e) => e.stopPropagation()}>{l ? <Link to={l.to}>{l.label}</Link> : <span className="text-muted">—</span>}</td>
                    <td className="max-w-[360px] truncate text-text2">{summary(a)}</td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        ) : null}
      </Card>
      <Modal open={!!open} onClose={() => setOpen(null)} title={open ? (ACTIONS[open.action] ?? open.action) : ''}>
        <span className="text-sm text-soft">
          {open?.actorName} (TG {open?.actorTelegramId}) · {open ? dateTime(open.createdAt) : ''}
        </span>
        <pre className="max-h-[60vh] overflow-auto rounded-xl bg-field p-3 font-mono text-xs leading-relaxed text-text2">{JSON.stringify(open?.payload ?? null, null, 2)}</pre>
      </Modal>
    </>
  );
}
